import React from "react";
import { Button, useToast } from "@chakra-ui/react";
import { CrudPage } from "../components/CrudPage";
import { SharedForm } from "../components/forms/SharedForm";
import { files, paymentSlips } from "../services/api";

const formatRial = (value?: number) =>
  value ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const columns = [
  {
    header: "سرمایه‌گذار",
    accessor: "request.investor",
    render: (item: any) =>
      item.request?.investor?.fullName ||
      [item.request?.investor?.firstName, item.request?.investor?.lastName]
        .filter(Boolean)
        .join(" ") ||
      item.request?.investor?.nationalId ||
      "—",
  },
  {
    header: "صندوق",
    accessor: "fundName",
    render: (item: any) => item.fundName || item.fundSeoRegisterNo || "—",
  },
  {
    header: "مبلغ",
    accessor: "amountRial",
    render: (item: any) => formatRial(item.amountRial),
  },
  {
    header: "بانک مبدا",
    accessor: "sourceBankName",
    render: (item: any) => item.sourceBankName || "—",
  },
  {
    header: "حساب مبدا",
    accessor: "sourceAccountNumber",
    render: (item: any) => item.sourceAccountNumber || "—",
  },
  {
    header: "حساب مقصد",
    accessor: "destinationAccountNumber",
    render: (item: any) => item.destinationAccountNumber || "—",
  },
  {
    header: "تاریخ انتقال",
    accessor: "transferedAt",
    render: (item: any) => formatDate(item.transferedAt),
  },
  {
    header: "فیش",
    accessor: "receiptFile",
    excludeFromExport: true,
  },
];

export const PaymentSlipsPage: React.FC = () => {
  const toast = useToast();

  const handleViewReceipt = async (item: any) => {
    const fileId = item.receiptFile?._id || item.receiptFile;
    if (!fileId) {
      return;
    }

    try {
      const response = await files.download(fileId);
      const fileBlob = new Blob([response.data], {
        type: item.receiptFile?.type || response.data?.type || "application/octet-stream",
      });
      const blobUrl = window.URL.createObjectURL(fileBlob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60_000);
    } catch (error: any) {
      toast({
        title: "خطا در دریافت فیش",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fields = [
    { label: "شناسه فایل رسید", name: "receiptFile", required: true },
    { label: "کد ثبت صندوق", name: "fundSeoRegisterNo", required: true },
    { label: "نام صندوق", name: "fundName", required: true },
    { label: "شماره حساب مبدا", name: "sourceAccountNumber", required: true },
    { label: "شماره حساب مقصد", name: "destinationAccountNumber", required: true },
    { label: "کد بانک مبدا", name: "sourceBankCode", required: true },
    { label: "نام بانک مبدا", name: "sourceBankName", required: true },
    { label: "شناسه بانک مبدا", name: "sourceBankId", required: true },
    { label: "تاریخ انتقال", name: "transferedAt", type: "date" },
    { label: "مبلغ", name: "amountRial", type: "number", required: true },
    { label: "یادداشت", name: "notes" },
  ];

  return (
    <CrudPage
      title="فیش‌های پرداخت"
      queryKey="paymentSlips"
      resourceName="paymentSlips"
      api={paymentSlips}
      showAddButton={false}
      enableEdit={false}
      enableDelete={false}
      enableView={false}
      columns={[
        ...columns.slice(0, 7),
        {
          ...columns[7],
          render: (item: any) => {
            const fileId = item.receiptFile?._id || item.receiptFile;
            if (!fileId) return "—";

            return (
              <Button
                size="xs"
                colorScheme="brand"
                variant="outline"
                onClick={() => void handleViewReceipt(item)}
              >
                مشاهده
              </Button>
            );
          },
        },
      ]}
      FormComponent={(props) => <SharedForm {...props} fields={fields} />}
    />
  );
};
